import * as React from "react";
import * as Fa from "react-icons/fa";
import { Link } from "react-router-dom";
import styled from "styled-components";
import logoImg from "../../../../images/logo-topbar.png";
import { dependencies } from "../../../dependencies";
import { getSkinUrls } from "../../../utils";
import LogOutIcon from "../../utils/Icons/LogOutIcon";

const Bar = styled.div(
  ({ theme }) => `
  display: flex;
  flex-direction: row;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 5rem;
  padding: 0 2rem 0 1rem
  background-color: ${theme.colors.headerBackground} !important;
  z-index: 1000;
  border-bottom: 0.5rem solid ${theme.colors.headerAccent} !important;

  @media (${theme.breakpoints.min800}) {
    height: 6.5rem;
    padding: 0 2rem 0 0;
  }

  @media print {
    display: none;
  }
`
);

const InnerBar = styled.div<{ fitContent?: boolean }>(
  ({ fitContent, theme }) => `
  align-items: center;
  display: flex;
  flex-direction: row;
  width: 100%;
  margin: ${fitContent ? "0 auto" : 0};
  max-width: ${fitContent ? theme.primaryTheme.maxWidth : "100%"};
`
);

const Logo = styled.img`
  display: flex;
  margin-left: 2rem;
`;

const ProfileMenu = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 0.5em;
  max-width: 100vw;
`;

type ProfileNameProps = Omit<IProfileProps, "accountName">;

const ProfileName = styled.span<ProfileNameProps>`
  display: none;
  color: ${({ theme }) => theme.colors.headerTextColor};
  font-weight: bold;
  margin-right: 1rem;
  max-width: 70vw;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (${({ theme }) => theme.breakpoints.min800}) {
    display: inline-block;
  }
`;

const ProfileIcon = styled(Fa.FaUserCircle)`
  width: 3rem;
  height: 3rem;
  color: ${({ theme }) => theme.colors.headerButton};

  :hover {
    opacity: 0.7;
  }
`;

const ExitIcon = styled(LogOutIcon)`
  color: ${({ theme }) => theme.colors.darkblue};
  width: 2.5rem;
  height: 2.5rem;
  margin-right: 0.5rem;
`;

const BarsIcon = styled(Fa.FaBars)`
  cursor: pointer;
  display: flex;
  min-width: 3rem;
  height: 4rem;
  color: ${({ theme }) => theme.colors.headerButton};

  @media (${({ theme }) => theme.breakpoints.min800}) {
    display: none;
  }
`;

interface IBarsButtonProps {
  onClick?: () => void;
}

const BarsButton: React.SFC<IBarsButtonProps> = ({ onClick }) => {
  return (
    <BarsBtn onClick={onClick}>
      <BarsIcon />
    </BarsBtn>
  );
};

const ExitLink = styled(Link)`
  color: ${({ theme }) => theme.colors.darkblue};
  align-items: center;
  display: flex;
  justify-content: center;
  margin: 0 0 0 auto;
  text-decoration: none;
`;

const BarsBtn = styled.button`
  background: none;
  color: inherit;
  border: none;
  padding: 0;
  font: inherit;
  cursor: pointer;
  outline: inherit;
  margin: 0;
`;

const ProfileList = styled.ul`
  display: flex;
  position: absolute;
  right: 0;
  top: 3rem;
  background-color: ${({ theme }) => theme.colors.white};
  list-style: none;
  padding: 0.1rem 1rem;
  border-radius: 0.2rem;
  box-shadow: 0 0 1rem 0 ${({ theme }) => theme.colors.grey};
`;

const ProfileItem = styled.li`
  padding: 0.5rem;

  :hover {
    opacity: 0.7;
  }
`;

interface IProfileProps {
  accountName: string;
}

const Profile: React.FC<IProfileProps> = (props: IProfileProps) => {
  const { accountName } = props;
  const [showMenu, setShowMenu] = React.useState<boolean>(false);
  const toggleMenu = () => setShowMenu(!showMenu);
  return (
    <ProfileMenu>
      <ProfileName>{accountName}</ProfileName>
      <ProfileIcon onClick={toggleMenu} />
      {showMenu && (
        <ProfileList>
          <ProfileItem>
            <ExitLink to="/logout">
              <ExitIcon />
              <span>Logout</span>
            </ExitLink>
          </ProfileItem>
        </ProfileList>
      )}
    </ProfileMenu>
  );
};

interface IAdminDashboardProps {
  fitContent?: boolean;
  onToggleMenuClick?: () => void;
  showToggleMenu: boolean;
  userName: string;
}

const { saltcomplianceLocalStorageKeys } = dependencies;

const getLogoImage = (): string => {
  const version = window.localStorage.getItem(saltcomplianceLocalStorageKeys.version);
  const root = window.localStorage.getItem(saltcomplianceLocalStorageKeys.root);
  const organizationId = window.localStorage.getItem(saltcomplianceLocalStorageKeys.organizationId);
  const fetchTimestamp = window.localStorage.getItem(saltcomplianceLocalStorageKeys.fetchTimestamp) || "";
  if (version && root && organizationId) {
    const { logo } = getSkinUrls(root, organizationId, version, fetchTimestamp);
    return logo.url;
  }
  return logoImg;
};

const AdminDashboard: React.FC<IAdminDashboardProps> = ({
  fitContent,
  onToggleMenuClick,
  showToggleMenu,
  userName,
}) => {
  const element = React.useRef<HTMLDivElement>(null);

  return (
    <Bar className="site-header-wrapper custom__accent" ref={element}>
      <InnerBar fitContent={fitContent}>
        <table className="site-header">
          <tbody>
            <tr>
              {showToggleMenu && (
                <td>
                  <BarsButton onClick={onToggleMenuClick} />
                </td>
              )}
              <td>
                <Link to="/dashboard">
                  <Logo className="logo" id="dashboard-logo" src={getLogoImage()} />
                </Link>
              </td>
              <td className="flex" />
              <td>
                <Profile accountName={userName} />
              </td>
            </tr>
          </tbody>
        </table>
      </InnerBar>
    </Bar>
  );
};

export default AdminDashboard;
